import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Usuario from '../model/Usuario/Usuario';
import keycloakService from '../services/keycloakService';
import useUserStore from '../stores/userStore';
import UsuarioViewModel from '../viewmodel/usuario/UsuarioViewModel';

type AuthState = 'loading' | 'authenticated' | 'unauthenticated';
type UserState = 'idle' | 'loading' | 'loaded' | 'error';

// Cache global para persistir entre instâncias do hook
const globalPermissionsCache = new Map<string, any>();

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [userState, setUserState] = useState<UserState>('idle');
  const { usuario, setUsuario, logout: clearUser } = useUserStore();
  const navigate = useNavigate();

  // Flags para controle de estado
  const hasLoaded = useRef(false);
  const hasLoadedPermissions = useRef(false);

  const loadUser = useCallback(async () => {
    const userId = keycloakService.tokenParsed?.sub;
    const userCpf = keycloakService.tokenParsed?.cpf;

    if (!keycloakService.isInitialized || !userCpf || !userId) {
      setUserState('error');
      return;
    }

    // Verifica se já carregou as permissões para este usuário (cache global)
    if (globalPermissionsCache.has(userId)) {
      const cachedPermissoes = globalPermissionsCache.get(userId);

      const user: Usuario = {
        id: userId,
        nome: keycloakService.tokenParsed!.name || '',
        username: keycloakService.tokenParsed!.preferred_username || '',
        email: keycloakService.tokenParsed!.email || null,
        perfil: { permissoes: cachedPermissoes },
      };

      setUsuario(user);
      setUserState('loaded');
      hasLoadedPermissions.current = true;
      return;
    }

    // Verifica se já está carregando para evitar chamadas duplicadas
    if (userState === 'loading' || hasLoadedPermissions.current) {
      return;
    }

    setUserState('loading');
    hasLoadedPermissions.current = true;

    try {
      const permissoes = await UsuarioViewModel.getPermissoesUsuario(userCpf);

      // Armazena no cache global
      globalPermissionsCache.set(userId, permissoes);

      const user: Usuario = {
        id: userId,
        nome: keycloakService.tokenParsed!.name || '',
        username: keycloakService.tokenParsed!.preferred_username || '',
        email: keycloakService.tokenParsed!.email || null,
        perfil: { permissoes },
      };

      setUsuario(user);
      setUserState('loaded');
    } catch (error) {
      // Em caso de erro, permite tentar novamente
      hasLoadedPermissions.current = false;

      if (process.env.NODE_ENV !== 'production') {
        console.error('Erro ao carregar usuário:', error);
      }
      setUserState('error');
    }
  }, [setUsuario, userState]);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    (async () => {
      try {
        const authenticated = await keycloakService.init();
        setAuthState(authenticated ? 'authenticated' : 'unauthenticated');

        if (authenticated) {
          await loadUser();
        }
      } catch {
        setAuthState('unauthenticated');
      }
    })();
  }, [loadUser]);

  const login = (redirectUri?: string) => keycloakService.login(redirectUri);

  const logout = () => {
    const userId = keycloakService.tokenParsed?.sub;

    // Remove do cache global ao fazer logout
    if (userId) {
      globalPermissionsCache.delete(userId);
    }

    clearUser();
    setAuthState('unauthenticated');
    setUserState('idle');
    hasLoadedPermissions.current = false;
    keycloakService.logout();
  };

  // Função para forçar recarregamento das permissões (útil para atualizações)
  const reloadPermissions = useCallback(async () => {
    const userId = keycloakService.tokenParsed?.sub;

    if (userId) {
      globalPermissionsCache.delete(userId);
    }

    hasLoadedPermissions.current = false;
    await loadUser();
  }, [loadUser]);

  // Função para limpar cache específico (útil para testes ou admin)
  const clearUserCache = useCallback((userId?: string) => {
    if (userId) {
      globalPermissionsCache.delete(userId);
    } else {
      // Limpa todo o cache se não especificar usuário
      globalPermissionsCache.clear();
    }
  }, []);

  return {
    isLoading: authState === 'loading',
    isAuthenticated: authState === 'authenticated',
    user: usuario,
    userLoading: userState === 'loading',
    userLoaded: userState === 'loaded',
    userError: userState === 'error',
    login,
    logout,
    loadUser,
    reloadPermissions,
    clearUserCache,
  };
};